<!--
  LwSwitch: the on/off control the Preferences page was missing (spec 49
  section 6, and the want TrucksEdit.vue recorded before it).

  A real `<button role="switch" aria-checked>`, not a styled `<input
  type="checkbox">` with its native box hidden. Two reasons. A switch and a
  checkbox mean different things to assistive technology -- a switch takes
  effect the moment it is flipped, which is exactly how a settings row that
  saves per change behaves, whereas a checkbox collects a choice for a form
  that will be submitted later. And a button draws its own track and thumb
  from tokens on every platform, where a checkbox's native face cannot be
  recoloured without `appearance: none` tricks that differ per browser.

  NAMED TWO WAYS. Standalone it carries its own visible text (the `label`
  prop or the default slot), rendered inside the button so the text IS the
  accessible name and clicking it flips the switch. Inside LwField it
  carries neither: the field's own `<label for>` names it (a button is a
  labelable element), the field's description is wired through
  `aria-describedby`, and the switch draws only the track. One of the two
  must be present; a bare switch with no name is the unlabelled control
  this library exists to delete.

  The on-state track is the product's one accent (--lw-amber-500, the same
  ground LwButton's primary stands on, with --lw-fg-on-accent for anything
  drawn over it); the off-state is the sunken surface behind a hairline.
  Disabled is a colour pair, never opacity (audit_mobile.md 3.10).
-->
<template>
  <button
    :id="id"
    type="button"
    role="switch"
    class="lw-c-switch"
    :class="{ 'lw-c-switch--on': modelValue, 'lw-c-switch--disabled': disabled }"
    :aria-checked="modelValue ? 'true' : 'false'"
    :aria-describedby="describedby"
    :disabled="disabled"
    @click="toggle"
  >
    <span class="lw-c-switch__track" aria-hidden="true">
      <span class="lw-c-switch__thumb" />
    </span>
    <span v-if="hasLabel" class="lw-c-switch__label">
      <slot>{{ label }}</slot>
    </span>
  </button>
</template>

<script>
export default {
  name: 'LwSwitch',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    // Visible text beside the track. Omit it only when something else names
    // the button: LwField's label, pointed at `id`.
    label: {
      type: String,
      default: null,
    },
    // Forwarded to the button so an external <label for> can find it.
    id: {
      type: String,
      default: null,
    },
    // Space-separated ids of the text that explains the switch (LwField's
    // description and error), forwarded as aria-describedby.
    describedby: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  computed: {
    hasLabel() {
      return !!this.label || !!this.$slots.default;
    },
  },
  methods: {
    // A disabled button never fires click in a browser, but a test (or a
    // programmatic .click()) can still reach here, and a disabled switch
    // that flips is a disabled switch in name only.
    toggle() {
      if (this.disabled) return;
      this.$emit('update:modelValue', !this.modelValue);
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../sass/mixins.generated';

.lw-c-switch {
  display: inline-flex;
  align-items: center;
  gap: var(--lw-space-3);
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  font: var(--lw-type-body);
  color: var(--lw-fg-primary);
  text-align: left;
  cursor: pointer;

  @include lw-focus-visible;

  &:disabled {
    color: var(--lw-fg-disabled);
    cursor: not-allowed;
  }
}

// The track: a pill twice as wide as it is tall, so the thumb has exactly
// its own width to travel. Sized off the spacing scale rather than in px so
// it tracks the same scale every other control's padding does.
.lw-c-switch__track {
  position: relative;
  flex: none;
  box-sizing: border-box;
  width: var(--lw-space-10);
  height: var(--lw-space-5);
  border-radius: var(--lw-radius-pill);
  background: var(--lw-bg-sunken);
  // 1px hairline: geometry with no token, the library's one allowed raw px.
  box-shadow: inset 0 0 0 1px var(--lw-line-strong);
  transition:
    background-color var(--lw-motion-fast) var(--lw-motion-ease),
    box-shadow var(--lw-motion-fast) var(--lw-motion-ease);
}

.lw-c-switch__thumb {
  position: absolute;
  // 2px inset on each side: the thumb sits inside the hairline with a
  // visible gap, which is what reads as a thumb on a track instead of a
  // pill cut in half. Geometry, not colour.
  top: 2px;
  left: 2px;
  width: calc(var(--lw-space-5) - 4px);
  height: calc(var(--lw-space-5) - 4px);
  border-radius: var(--lw-radius-pill);
  background: var(--lw-bg-paper);
  box-shadow: 0 0 0 1px var(--lw-line-strong);
  transition: transform var(--lw-motion-fast) var(--lw-motion-ease);
}

.lw-c-switch--on {
  .lw-c-switch__track {
    background: var(--lw-amber-500);
    box-shadow: inset 0 0 0 1px var(--lw-amber-600);
  }

  .lw-c-switch__thumb {
    // Travels the track's width minus its own: 40 - 20 = one space-5.
    transform: translateX(var(--lw-space-5));
    background: var(--lw-bg-paper);
    box-shadow: 0 0 0 1px var(--lw-amber-600);
  }
}

.lw-c-switch:hover:not(:disabled) .lw-c-switch__track {
  box-shadow: inset 0 0 0 1px var(--lw-fg-secondary);
}

.lw-c-switch--on:hover:not(:disabled) .lw-c-switch__track {
  background: var(--lw-amber-600);
  box-shadow: inset 0 0 0 1px var(--lw-amber-800);
}

// Disabled: both states go to the inset fill behind a hairline, with the
// on-state still legible as "on" by thumb position alone. A colour pair,
// never opacity over a fill.
.lw-c-switch:disabled,
.lw-c-switch--on:disabled {
  .lw-c-switch__track {
    background: var(--lw-bg-inset);
    box-shadow: inset 0 0 0 1px var(--lw-line-hairline);
  }

  .lw-c-switch__thumb {
    background: var(--lw-fg-disabled);
    box-shadow: none;
  }
}

.lw-c-switch__label {
  font: var(--lw-type-body);
}

@media (prefers-reduced-motion: reduce) {
  .lw-c-switch__track,
  .lw-c-switch__thumb {
    transition: none;
  }
}
</style>
